import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '../constants';
import ThemeToggle from './ThemeToggle';
import { authFetch } from '../utils/api';

// Wall clock is rendered in US Eastern time so it agrees with the
// decision/trade timestamps in the rest of the dashboard.
const TZ = 'America/New_York';

function _readUser() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function _userLabel(u) {
  if (!u) return '';
  return u.full_name || u.name
       || [u.first_name, u.last_name].filter(Boolean).join(' ')
       || u.username || u.email || 'User';
}

function _userInitial(u) {
  const lbl = _userLabel(u);
  return (lbl[0] || '?').toUpperCase();
}

// Sequencing tab is restricted to specific operators. Matched by username first
// (stable) then full_name. NB: Gbadebo Arowolo's username is 'seun', not 'gbadebo'.
const SEQ_ALLOWED_USERNAMES = new Set(['deremi.ogunsipe', 'admin', 'seun']);
const SEQ_ALLOWED_FULLNAMES = new Set(['deremi ogunsipe', 'micheal omojola', 'gbadebo arowolo']);
function canSeeSequencing(u) {
  if (!u) return false;
  const uname = String(u.username || '').replace(/^@/, '').trim().toLowerCase();
  if (SEQ_ALLOWED_USERNAMES.has(uname)) return true;
  const fn = String(u.full_name || u.name || [u.first_name, u.last_name].filter(Boolean).join(' ')).trim().toLowerCase();
  return SEQ_ALLOWED_FULLNAMES.has(fn);
}

// ─────────────────────────────────────────────────────────────────────
// Top-nav page buttons — expanded inline (no longer hidden in a dropdown).
// GBEMI Monitor + MansaMonitor.jsx removed 2026-06-10 alongside the
// full GBEMI strategy wipe.
// ─────────────────────────────────────────────────────────────────────
const NAV_PAGES = [
  { id: 'mansa-monitor',   label: 'Briefings',            color: '#3b82f6' },
  { id: 'sequencing',      label: 'Sequencing',           color: '#2dd4bf' },
  // P1-S1 Monitor hidden 2026-06-08 alongside the VISIBLE_STRATEGIES removal.
  // Re-add the line below to restore the tab when P1-S1 is resumed.
  // { id: 'p1s1-monitor',    label: 'P1-S1 Monitor',        color: '#26a69a' },
  // EMEKA Monitor removed 2026-06-24 — strategy retired (operator request).
  // NCHE Monitor removed 2026-06-24 — strategy retired (operator request).
  // KUNBI hidden 2026-06-19 — discontinued (paused via KUNBI_PAUSED=1). Re-add to restore.
  // { id: 'kunbi-monitor',   label: 'KUNBI Monitor',        color: '#d97706' },
  // FAVOLA + GEMINI Monitors removed 2026-06-29 (Option A full wipe).
  // Imperial hidden 2026-06-19 — paused (imperial_kill_switch). Re-add to restore.
  // { id: 'imperial-monitor',  label: 'Imperial Monitor',   color: '#eab308' },
  // TIER 1 hidden 2026-06-29 — deactivated pending go-ahead (backend TIER1_PAUSED=1 + tier1-walk-open beat disabled). Re-add to restore.
  // { id: 'tier1-monitor',     label: 'TIER 1 Monitor',     color: '#0ea5e9' },
  // WURA retired 2026-06-29 (Director NQ Desk v2). Backend WURA_PAUSED=1.
  // { id: 'wura-monitor',      label: 'WURA Monitor',       color: '#ca8a04' },
  // IYIPADA Monitor hidden 2026-06-22 by operator request. Backend paused via
  // IYIPADA_PAUSED=1 on prod. Re-add the line below to restore the tab.
  // { id: 'iyipada-monitor',   label: 'IYIPADA Monitor',    color: '#e11d48' },
  { id: 'osupa-monitor',     label: 'OSUPA Monitor',      color: '#6366f1' },
  { id: 'osupa-short-monitor', label: 'OSUPA SHORT Monitor', color: '#a855f7' },
  { id: 'anyanwu-monitor',   label: 'ANYANWU Monitor',    color: '#f59e0b' },
  { id: 'iyanu-monitor',     label: 'IYANU Monitor',      color: '#22c55e' },
  { id: 'juyi-monitor',      label: 'JUYI Monitor',       color: '#a855f7' },
  { id: 'isale-monitor',     label: 'ISALE Monitor',      color: '#ef4444' },
  { id: 'nuit-monitor',      label: 'NUIT Monitor',       color: '#eab308' },
  // ── ES Desk (cert ES-DESK-…8392E873) — SHADOW / flat-1c, deployed 2026-06-29 ──
  { id: 'sirius-monitor',    label: 'SIRIUS Monitor',     color: '#a855f7' },
  { id: 'rigel-monitor',     label: 'RIGEL Monitor',      color: '#2962ff' },
  { id: 'mars-monitor',      label: 'MARS Monitor',       color: '#e11d48' },
  { id: 'isinmi-monitor',    label: 'ISINMI Monitor',     color: '#f97316' },
  { id: 'saturn-monitor',    label: 'SATURN Monitor',     color: '#0ea5e9' },
  { id: 'venus-monitor',     label: 'VENUS Monitor',      color: '#0d9488' },
  // TIER 2 / TIER 5 Monitor tabs removed 2026-06-16 (tiers removed from app).
  { id: 'backtest',        label: 'Backtest',             color: '#f59e0b' },
  // Positions tab hidden 2026-06-10 — backend mansa_monitor endpoints removed
  // with the GBEMI wipe. Re-enable once a fresh per-strategy positions view
  // is wired up.
  // { id: 'open-positions',  label: 'Positions',            color: '#f97316' },
];

function PageButton({ page, active, onClick }) {
  return (
    <button
      onClick={onClick}
      title={page.label}
      className="nav-page-btn"
      style={{
        display: 'flex', alignItems: 'center',
        padding: '4px 10px', borderRadius: 4, cursor: 'pointer', border: 'none',
        fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
        background: active ? `${page.color}22` : `${page.color}14`,
        color: active ? page.color : `${page.color}d9`,
        borderBottom: active ? `2px solid ${page.color}` : '2px solid transparent',
        whiteSpace: 'nowrap',
      }}
    >
      {page.label}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// NavPages — monitor tabs with a "priority+" overflow. It measures how many
// tabs fit the available width, shows those inline, and tucks the rest into a
// "More ▾" dropdown. Works at every size: on large screens all tabs fit (no
// More); on small screens the overflow collapses into the dropdown instead of
// wrapping or horizontally scrolling.
// ─────────────────────────────────────────────────────────────────────
function NavPages({ activePage, onPageChange }) {
  // Filter restricted tabs by the current user (Sequencing → allowlist only).
  const PAGES = NAV_PAGES.filter(p => p.id !== 'sequencing' || canSeeSequencing(_readUser()));
  const wrapRef = useRef(null);
  const measureRef = useRef(null);
  const moreRef = useRef(null);
  const [nVisible, setNVisible] = useState(PAGES.length);
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    function recompute() {
      const wrap = wrapRef.current, measure = measureRef.current;
      if (!wrap || !measure) return;
      const avail = wrap.clientWidth;
      const kids = Array.from(measure.children);
      const GAP = 6;
      const MORE_W = 92;            // room reserved for the "More ▾" button + gap
      let used = 0, fit = 0;
      for (let i = 0; i < kids.length; i++) {
        const w = kids[i].getBoundingClientRect().width + GAP;
        const reserve = (i < kids.length - 1) ? MORE_W : 0;   // last item needs no More
        if (used + w <= avail - reserve) { used += w; fit++; }
        else break;
      }
      setNVisible(Math.max(1, Math.min(PAGES.length, fit)));
    }
    recompute();
    const t = setTimeout(recompute, 60);   // re-measure once fonts/layout settle
    let ro;
    if (typeof ResizeObserver !== 'undefined' && wrapRef.current) {
      ro = new ResizeObserver(recompute);
      ro.observe(wrapRef.current);
    }
    window.addEventListener('resize', recompute);
    return () => {
      clearTimeout(t);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', recompute);
    };
  }, []);

  // Close the More menu on outside click / Escape
  useEffect(() => {
    if (!moreOpen) return;
    const onClick = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    const onKey   = (e) => { if (e.key === 'Escape') setMoreOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onKey); };
  }, [moreOpen]);

  const select = (id) => {
    setMoreOpen(false);
    onPageChange && onPageChange(activePage === id ? 'dashboard' : id);
  };

  const visible  = PAGES.slice(0, nVisible);
  const overflow = PAGES.slice(nVisible);
  const activeInOverflow = overflow.some(p => p.id === activePage);

  return (
    <div ref={wrapRef} className="nav-pages">
      {/* Hidden measuring row — all tabs, off-flow, to size the overflow split */}
      <div ref={measureRef} aria-hidden className="nav-pages-measure">
        {PAGES.map(p => <PageButton key={p.id} page={p} active={false} onClick={() => {}} />)}
      </div>

      {visible.map(p => (
        <PageButton key={p.id} page={p} active={activePage === p.id} onClick={() => select(p.id)} />
      ))}

      {overflow.length > 0 && (
        <div ref={moreRef} className="nav-more" style={{ position: 'relative', flex: '0 0 auto' }}>
          <button
            onClick={() => setMoreOpen(v => !v)}
            className="nav-page-btn"
            title="More monitors"
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '4px 10px', borderRadius: 4, cursor: 'pointer', border: 'none',
              fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
              background: activeInOverflow ? `${T.bull}22` : 'rgba(124,58,237,0.14)',
              color: activeInOverflow ? T.bull : (T.textDim || T.text),
              borderBottom: activeInOverflow ? `2px solid ${T.bull}` : '2px solid transparent',
              whiteSpace: 'nowrap',
            }}
          >
            More <span style={{ opacity: 0.65 }}>({overflow.length})</span>
            <svg width="9" height="9" viewBox="0 0 9 9"><path d="M2 3.5 L4.5 6 L7 3.5" stroke="currentColor" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>

          {moreOpen && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 1000,
              minWidth: 190, maxHeight: '72vh', overflowY: 'auto',
              background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8,
              boxShadow: '0 10px 30px rgba(0,0,0,0.18)', padding: 4,
            }}>
              {overflow.map(p => (
                <button
                  key={p.id}
                  onClick={() => select(p.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                    padding: '8px 10px', borderRadius: 6, cursor: 'pointer', border: 'none',
                    background: activePage === p.id ? `${p.color}24` : 'transparent',
                    color: activePage === p.id ? p.color : T.text,
                    fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', whiteSpace: 'nowrap',
                  }}
                  onMouseOver={(e) => { if (activePage !== p.id) e.currentTarget.style.background = `${p.color}14`; }}
                  onMouseOut={(e)  => { if (activePage !== p.id) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: p.color, flexShrink: 0 }} />
                  {p.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Profile dropdown — now also home to the utility controls that used to
// sit on the navbar (Health, Sound, Test, Light/Dark, Poll status).
// Keeping them grouped here keeps the top bar scannable for page nav.
// ─────────────────────────────────────────────────────────────────────
function ProfileMenu({
  user, onPageChange, alert,
  isHealthy, wsConnected, online, lastPoll, pollPct, feedColor,
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click + Escape
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey   = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleLogout = async () => {
    setOpen(false);
    try { await authFetch('/authentication/logout/', { method: 'POST' }); } catch {}
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    navigate('/login');
  };

  if (!user) return null;
  const connLabel = wsConnected ? 'LIVE' : online ? 'POLL' : 'OFFLINE';
  const connColor = isHealthy ? T.bull : T.bear;

  const rowStyle = {
    width: '100%', textAlign: 'left',
    padding: '10px 14px', cursor: 'pointer', border: 'none',
    background: 'transparent',
    fontSize: 12, fontWeight: 600,
    display: 'flex', alignItems: 'center', gap: 8,
    color: T.text,
  };
  const sectionLabel = {
    padding: '8px 14px 4px', fontSize: 9, fontWeight: 700,
    color: T.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase',
  };
  const divider = { height: 1, background: T.border, margin: '4px 0' };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(v => !v)}
        className="profile-trigger"
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '3px 8px 3px 3px', borderRadius: 999, cursor: 'pointer',
          border: `1px solid ${T.border}`, background: 'transparent',
        }}
        title={`Signed in as ${_userLabel(user)}`}
      >
        <span style={{
          width: 22, height: 22, borderRadius: '50%',
          background: 'rgba(124,58,237,0.18)', color: '#7c3aed',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 700,
        }}>{_userInitial(user)}</span>
        <span className="profile-name" style={{ fontSize: 11, color: T.textDim, maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {_userLabel(user)}
        </span>
        <svg width="9" height="9" viewBox="0 0 9 9" style={{ flexShrink: 0, opacity: 0.6 }}>
          <path d="M2 3.5 L4.5 6 L7 3.5" stroke="currentColor" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <div className="profile-menu-panel" style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0,
          minWidth: 260, zIndex: 1000,
          background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8,
          boxShadow: '0 10px 30px rgba(0,0,0,0.18)', overflow: 'hidden',
        }}>
          {/* User card */}
          <div style={{ padding: '12px 14px', borderBottom: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.textDim }}>
              {_userLabel(user)}
            </div>
            {user.email && (
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>
                {user.email}
              </div>
            )}
            {user.role && (
              <div style={{
                display: 'inline-block', marginTop: 6,
                fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                padding: '2px 6px', borderRadius: 4,
                background: 'rgba(124,58,237,0.12)', color: '#7c3aed',
              }}>
                {user.role}
              </div>
            )}
          </div>

          {/* Status section — Connection (LIVE/POLL/OFFLINE) + poll bar */}
          <div style={sectionLabel}>Connection</div>
          <div style={{ padding: '6px 14px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className={`status-dot ${isHealthy ? 'live' : 'offline'}`} />
              <span style={{ fontSize: 11, fontWeight: 700, color: connColor, letterSpacing: '0.06em' }}>
                {connLabel}
              </span>
              {lastPoll && (
                <span className="mono" style={{ marginLeft: 'auto', fontSize: 10, color: T.textMuted }}>
                  {lastPoll.toLocaleTimeString('en-US', { hour12: false, timeZone: TZ })}
                </span>
              )}
            </div>
            <div className="poll-bar-track" style={{ width: '100%' }}>
              <div className="poll-bar-fill" style={{ width: `${pollPct}%`, background: feedColor }} />
            </div>
          </div>

          <div style={divider} />

          {/* Tools section — Health page, Sound toggle, Test sound, Theme toggle */}
          <div style={sectionLabel}>Tools</div>

          <button
            style={rowStyle}
            onClick={() => { setOpen(false); onPageChange && onPageChange('system-health'); }}
            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(16,185,129,0.08)'}
            onMouseOut={(e)  => e.currentTarget.style.background = 'transparent'}
          >
            <svg width="13" height="13" viewBox="0 0 11 11" fill="none">
              <path d="M1.5 6 H3.5 L4.5 3 L6.5 9 L7.5 6 H9.5"
                    stroke="#10b981" strokeWidth="1.4" fill="none"
                    strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Health
          </button>

          <button
            style={rowStyle}
            onClick={() => { setOpen(false); onPageChange && onPageChange('ai-review'); }}
            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(124,58,237,0.08)'}
            onMouseOut={(e)  => e.currentTarget.style.background = 'transparent'}
          >
            <svg width="13" height="13" viewBox="0 0 12 12" fill="none">
              <path d="M6 1 L7.2 4.8 L11 6 L7.2 7.2 L6 11 L4.8 7.2 L1 6 L4.8 4.8 Z"
                    fill="#7c3aed" stroke="#7c3aed" strokeWidth="0.5" strokeLinejoin="round"/>
            </svg>
            AI Review
          </button>

          <button
            style={rowStyle}
            onClick={() => { setOpen(false); onPageChange && onPageChange('signal-audit'); }}
            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(220,38,38,0.08)'}
            onMouseOut={(e)  => e.currentTarget.style.background = 'transparent'}
          >
            <svg width="13" height="13" viewBox="0 0 12 12" fill="none">
              <circle cx="5" cy="5" r="3" stroke="#dc2626" strokeWidth="1.4" fill="none"/>
              <path d="M7.5 7.5 L10 10" stroke="#dc2626" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
            Signal Audit
          </button>

          {/* Sound + Test live inline as two pills so the user can mute/unmute
              and fire a test alarm without leaving the menu. */}
          <div style={{ display: 'flex', gap: 8, padding: '6px 14px 10px' }}>
            <button
              onClick={() => alert.toggleMute && alert.toggleMute()}
              style={{
                flex: 1, padding: '6px 10px', borderRadius: 4,
                border: `1px solid ${T.border}`, cursor: 'pointer',
                background: alert.muted ? 'rgba(220,38,38,0.10)' : 'rgba(16,185,129,0.10)',
                color: alert.muted ? T.bear : T.bull,
                fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}
              title={alert.muted ? 'Unmute alerts' : 'Mute alerts'}
            >
              {alert.muted ? '🔕' : '🔔'} {alert.muted ? 'Muted' : 'Sound'}
            </button>
            <button
              onClick={() => alert.testSound && alert.testSound()}
              style={{
                flex: 1, padding: '6px 10px', borderRadius: 4,
                border: `1px solid ${T.border}`, cursor: 'pointer',
                background: 'transparent', color: T.textDim,
                fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
              }}
              title="Test the alert sound"
            >
              Test
            </button>
          </div>

          {/* Theme toggle — reuse the existing component so its logic
              (localStorage + page reload) stays in one place. */}
          <div style={{ padding: '0 14px 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <ThemeToggle />
            <span style={{ fontSize: 11, color: T.textMuted }}>Theme</span>
          </div>

          <div style={divider} />

          {/* Admin (role-gated) + Sign out */}
          {user.role === 'admin' && (
            <button
              onClick={() => { setOpen(false); navigate('/admin'); }}
              style={{ ...rowStyle, color: '#7c3aed' }}
              onMouseOver={(e) => e.currentTarget.style.background = 'rgba(124,58,237,0.08)'}
              onMouseOut={(e)  => e.currentTarget.style.background = 'transparent'}
            >
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                <path d="M6.5 1.5 L11 4 V7.5 c0 2.5 -2 4 -4.5 4.5 c-2.5 -0.5 -4.5 -2 -4.5 -4.5 V4 z"
                      stroke="currentColor" strokeWidth="1.2" fill="none" strokeLinejoin="round"/>
                <path d="M4.8 6.5 L6.2 7.9 L8.4 5.4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
              Admin Dashboard
            </button>
          )}

          <button
            onClick={handleLogout}
            style={{ ...rowStyle, color: T.bear }}
            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(220,38,38,0.08)'}
            onMouseOut={(e)  => e.currentTarget.style.background = 'transparent'}
          >
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
              <path d="M5 2 H3 a1 1 0 0 0 -1 1 v7 a1 1 0 0 0 1 1 h2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              <path d="M8 4 L10.5 6.5 L8 9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M5 6.5 H10.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default function Navbar({ feedColor, isHealthy, wsConnected, online, lastPoll, pollPct, alert, activePage, onPageChange }) {
  const [user, setUser] = useState(_readUser);

  // Re-sync if another tab logs in/out
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'user' || e.key === 'token') setUser(_readUser());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const now = new Date();
  const dateStr = now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: TZ });
  const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: TZ });

  return (
    <nav className="navbar">
      <div
        className="nav-brand"
        onClick={() => {
          // If we're already on the main dashboard, do a real page refresh
          // so the user gets fresh data + a clean state. Otherwise, navigate
          // back to dashboard (cheaper than a full reload).
          if (activePage === 'dashboard' || activePage == null) {
            window.location.reload();
          } else if (onPageChange) {
            onPageChange('dashboard');
          }
        }}
        title={activePage === 'dashboard' ? 'Refresh dashboard' : 'Back to dashboard'}
        style={{ cursor: 'pointer' }}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0 }}>
          <path d="M2 13 L6 8 L10 10 L14 4 L16 6" stroke={T.bull} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          <circle cx="14" cy="4" r="1.5" fill={T.bull}/>
        </svg>
        <span className="brand-name">Imole</span>
      </div>

      {/* Monitor tabs — fit inline, overflow collapses into a "More" dropdown */}
      <NavPages activePage={activePage} onPageChange={onPageChange} />

      <div className="nav-actions">
        <span className="nav-date" style={{ fontSize: 11, color: T.textDim }}>{dateStr}</span>
        <span className="mono nav-time" style={{ fontSize: 11, color: T.textMuted }}>{timeStr}</span>

        {user && (
          <>
            <div style={{ width: 1, height: 16, background: T.border }} />
            <ProfileMenu
              user={user}
              onPageChange={onPageChange}
              alert={alert}
              isHealthy={isHealthy}
              wsConnected={wsConnected}
              online={online}
              lastPoll={lastPoll}
              pollPct={pollPct}
              feedColor={feedColor}
            />
          </>
        )}
      </div>
    </nav>
  );
}
